<template>
    <div id="safe-area-top"></div>
    <v-app
        :class="{
            'set-bg': isDeepLink || invalidDeepLink,
        }">
        <v-progress-circular
            v-if="isDeepLink && !invalidDeepLink"
            class="tw-fixed tw-left-1/2 tw-top-1/2 tw-z-[10011] -tw-translate-x-1/2 -tw-translate-y-1/2"
            color="primary"
            indeterminate
            size="64">
        </v-progress-circular>
        <template v-if="invalidDeepLink">
            <v-card
                class="tw-left-1/2 tw-top-1/2 tw-z-[10010] tw-w-[90vw] tw-max-w-[400px] -tw-translate-x-1/2 -tw-translate-y-1/2"
                position="fixed">
                <v-card-text class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-text-center">
                    <v-icon
                        color="red"
                        size="64"
                        >mdi-alert-circle-outline</v-icon
                    >
                    <p>{{ t('app.invalidDeepLink') }}</p>
                    <div>
                        <Button
                            color="primary"
                            @click="appStore.setInvalidDeepLink(false)">
                            {{ t('shared.close') }}
                        </Button>
                    </div>
                </v-card-text>
            </v-card>
        </template>
        <router-view v-slot="{ Component }">
            <keep-alive>
                <component :is="Component" />
            </keep-alive>
        </router-view>
        <PromptUpdate />
        <PWAInstallBanner />
    </v-app>
    <div id="safe-area-bottom"></div>
</template>

<script lang="ts" setup>
    import { SafeArea } from 'capacitor-plugin-safe-area';
    import { Capacitor } from '@capacitor/core';
    import { App as CapacitorApp } from '@capacitor/app';
    import { PushNotifications } from '@capacitor/push-notifications';
    import PromptUpdate from '@/components/PromptUpdate.vue';
    import PWAInstallBanner from '@/components/PWAInstallBanner.vue';
    // import { useWindowSize } from '@vueuse/core';
    import { useI18n } from 'vue-i18n';
    import _ from 'lodash';
    import { useAppStore } from './store/app';
    import { UI_CONSTANTS } from '@/configs/constants';
    import { APP_BUILD_NUMBER, APP_NAME } from './configs/app';

    // const { width, height } = useWindowSize();
    const $router = useRouter();
    const { t } = useI18n();
    const appStore = useAppStore();

    // Track navigation direction with size limit
    const historyStack: {
        path: string;
        scrollPosition: { x: number; y: number };
    }[] = [];

    const invalidDeepLink = computed(() => appStore.isInvalidDeepLink);
    // const invalidDeepLink = ref(true);
    const isDeepLink = computed(() => appStore.isDeepLink);

    $router.afterEach((to, from) => {
        // check if the to is the last item in the history stack
        const lastItem = historyStack[historyStack.length - 1];
        if (lastItem && lastItem.path === to.path) {
            // If the last item matches the current route, remove it
            historyStack.pop();
            // set the window scroll position to the last saved position
            setTimeout(() => {
                window.scrollTo(lastItem.scrollPosition.x, lastItem.scrollPosition.y);
            }, UI_CONSTANTS.SCROLL_RESTORE_DELAY_MS);
        } else {
            // Otherwise, add the current route to the history stack
            historyStack.push({
                path: from.path,
                scrollPosition: { x: window.scrollX, y: window.scrollY },
            });

            // Limit history stack size to prevent memory leaks
            if (historyStack.length > UI_CONSTANTS.MAX_HISTORY_ENTRIES) {
                historyStack.shift(); // Remove oldest entry
            }

            setTimeout(() => {
                window.scrollTo(0, 0);
            }, UI_CONSTANTS.SCROLL_RESTORE_DELAY_MS);
        }

        if (!['MotorPage', 'NewsPage', 'News'].includes(to.name as string)) {
            if (!!to.meta?.title && typeof to.meta.title === 'string') {
                document.title = `${t(to.meta.title)} - ${APP_NAME}`;
            } else {
                document.title = APP_NAME;
            }
        }
    });

    CapacitorApp.addListener('backButton', () => {
        $router.go(-1);
    });
    if (Capacitor.isNativePlatform()) {
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
            const postId = _.get(notification, 'notification.data.id', null);
            if (postId) {
                $router.push({
                    name: 'NewsPage',
                    params: { id: postId },
                });
            }
        });
    } else if (navigator?.serviceWorker?.onmessage) {
        navigator.serviceWorker.onmessage = (event) => {
            if (event.data.messageType === 'notification-clicked') {
                // window.location.href = event.data.notification.click_action;
                const postId = _.get(event.data, 'data.id', null);
                if (postId) {
                    $router.push({
                        name: 'NewsPage',
                        params: { id: postId },
                    });
                }
            }
        };
    }

    onMounted(() => {
        SafeArea.getSafeAreaInsets().then(({ insets }) => {
            for (const [key, value] of Object.entries(insets)) {
                document.documentElement.style.setProperty(`--safe-area-${key}`, `${value}px`);
            }
        });
    });

    appStore.setAppBuildNumber(APP_BUILD_NUMBER);

    // Cleanup function to prevent memory leaks
    onUnmounted(() => {
        // Clear history stack
        historyStack.length = 0;

        // Remove event listeners if any were added
        // (Capacitor listeners are automatically cleaned up)
    });
</script>

<style lang="scss">
    .set-bg::before {
        content: '';
        position: fixed;
        top: var(--safe-area-top, 0);
        left: 0;
        right: 0;
        bottom: var(--safe-area-bottom, 0);
        background-color: rgba(255, 255, 255, 0.5);
        z-index: 1010;
    }
</style>
